import { useMemo, useState, useEffect } from 'react';

/**
 * Get language from localStorage (single source of truth)
 */
const getLanguage = () => {
  return localStorage.getItem('preferredLanguage') || 'nl';
};

/**
 * Custom hook for reactive translations
 * Uses localStorage as single source of truth with event-based reactivity
 *
 * Usage:
 * import translations from './translations.json';
 * const t = useTranslations(translations);
 * // Or with namespace:
 * const t = useTranslations(translations, 'profile');
 *
 * @param {Object} translations - Translation object with language keys (nl, en, fr)
 * @param {string} namespace - Optional namespace to access nested translations
 * @returns {Object} Translation object for current language
 */
const useTranslations = (translations, namespace = null) => {
  const [currentLanguage, setCurrentLanguage] = useState(getLanguage);

  useEffect(() => {
    // Listen for language changes within this tab
    const handleLanguageChange = () => {
      setCurrentLanguage(getLanguage());
    };

    // Listen for changes from other tabs
    const handleStorage = (e) => {
      if (e.key === 'preferredLanguage') {
        setCurrentLanguage(e.newValue || 'nl');
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return useMemo(() => {
    if (!translations) return {};

    if (namespace) {
      return translations[currentLanguage]?.[namespace] ||
             translations['nl']?.[namespace] ||
             {};
    }

    return translations[currentLanguage] || translations['nl'] || {};
  }, [translations, currentLanguage, namespace]);
};

export default useTranslations;
